import { useEffect, useState, useCallback } from 'react';
import { TrendingDown, Package, MapPin, Clock, MoreHorizontal, Eye, Trash2, AlertTriangle, BarChart3, CheckCircle, Link as LinkIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  MetricCard,
  KPIGrid,
  ConfirmDialog,
  StatusBadge,
} from '@/components/common';
import {
  consumptionApi,
  ConsumptionRecord,
  CreateConsumptionDto,
  QueryConsumptionParams,
  ConsumptionDashboard,
  ConsumptionTrendItem,
} from '@/api/services/consumption.api';
import { inventoryApi, InventoryItem, locationsApi, Location } from '@/api/services';
import { getErrorMessage } from '@/api/client';

interface RecordConsumptionFormData {
  // Core fields
  itemId: string;
  locationId: string;
  quantity: string;
  unit: string;
  workOrderId: string;
  notes: string;

  // OFS-specific fields
  plannedQuantity: string;
  varianceReason: string;
  jobId: string;
  jobName: string;
  operationPhase: string;
  consumptionRate: string;
  rateUnit: string;
  baseMetric: string;
  certificateNumbers: string;
}

const emptyFormData: RecordConsumptionFormData = {
  itemId: '',
  locationId: '',
  quantity: '',
  unit: 'EA',
  workOrderId: '',
  notes: '',
  // OFS fields
  plannedQuantity: '',
  varianceReason: '',
  jobId: '',
  jobName: '',
  operationPhase: '',
  consumptionRate: '',
  rateUnit: '',
  baseMetric: '',
  certificateNumbers: '',
};

export default function ConsumptionTracking() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<ConsumptionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<ConsumptionDashboard | null>(null);
  const [_trends, setTrends] = useState<ConsumptionTrendItem[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // OFS KPIs
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState<number>(0);
  const [averageVariance, setAverageVariance] = useState<number>(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    record?: ConsumptionRecord;
  }>({ open: false });
  const [deleting, setDeleting] = useState(false);

  // Detail sheet
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ConsumptionRecord | null>(null);

  // Record consumption form
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<RecordConsumptionFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  // Variance tracking
  const [variancePercentage, setVariancePercentage] = useState<number>(0);
  const [requiresApproval, setRequiresApproval] = useState<boolean>(false);

  // Reference data
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: QueryConsumptionParams = {
        search: searchQuery || undefined,
        page: pagination.page,
        limit: pagination.limit,
      };

      if (locationFilter !== 'all') {
        params.locationId = locationFilter;
      }

      if (dateFilter !== 'all') {
        const now = new Date();
        if (dateFilter === 'today') {
          params.startDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        } else if (dateFilter === 'week') {
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          params.startDate = weekAgo.toISOString();
        } else if (dateFilter === 'month') {
          const monthAgo = new Date();
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          params.startDate = monthAgo.toISOString();
        }
      }

      const [recordsResponse, dashboardResponse, trendsResponse, pendingApprovalsResponse, varianceResponse] = await Promise.all([
        consumptionApi.getAll(params),
        consumptionApi.getDashboard(),
        consumptionApi.getTrends({ periods: 14, interval: 'daily' }),
        consumptionApi.getPendingApprovals().catch(() => ({ data: [] })),
        consumptionApi.getVarianceAnalysis({
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date().toISOString()
        }).catch(() => ({ data: { records: [], summary: { averageVariancePercentage: 0 } } })),
      ]);

      setRecords(recordsResponse.data.data);
      setPagination(recordsResponse.data.pagination);
      setDashboard(dashboardResponse.data);
      setTrends(trendsResponse.data);
      setPendingApprovalsCount(pendingApprovalsResponse.data.length);
      setAverageVariance(varianceResponse.data.summary.averageVariancePercentage);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, locationFilter, dateFilter, pagination.page, pagination.limit]);

  const fetchReferenceData = async () => {
    try {
      setLoadingRefs(true);
      const [itemsRes, locationsRes] = await Promise.all([
        inventoryApi.getAll({ limit: 500 }),
        locationsApi.getAll({ limit: 500 }),
      ]);
      setInventoryItems(itemsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    } finally {
      setLoadingRefs(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchReferenceData();
  }, [fetchData]);

  // Calculate variance in real-time
  useEffect(() => {
    const planned = parseFloat(formData.plannedQuantity);
    const actual = parseFloat(formData.quantity);

    if (planned && actual && planned > 0) {
      const variance = ((actual - planned) / planned) * 100;
      setVariancePercentage(variance);
      setRequiresApproval(Math.abs(variance) > 10);
    } else {
      setVariancePercentage(0);
      setRequiresApproval(false);
    }
  }, [formData.plannedQuantity, formData.quantity]);

  const handleViewDetail = (record: ConsumptionRecord) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteDialog.record) return;

    try {
      setDeleting(true);
      await consumptionApi.delete(deleteDialog.record.id);
      toast.success('Consumption record deleted successfully');
      setDeleteDialog({ open: false });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenForm = () => {
    setFormData(emptyFormData);
    setFormOpen(true);
  };

  const handleItemChange = (itemId: string) => {
    const item = inventoryItems.find((i) => i.id === itemId);
    setFormData((prev) => ({
      ...prev,
      itemId,
      unit: item?.unit || 'EA',
    }));
  };

  const handleSubmit = async () => {
    if (!formData.itemId || !formData.locationId || !formData.quantity) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate variance reason if required
    if (requiresApproval && !formData.varianceReason) {
      toast.error('Variance reason is required for variances > 10%');
      return;
    }

    try {
      setSaving(true);
      const dto: CreateConsumptionDto = {
        // Core fields
        itemId: formData.itemId,
        locationId: formData.locationId,
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        workOrderId: formData.workOrderId || undefined,
        notes: formData.notes || undefined,

        // OFS-specific fields
        plannedQuantity: formData.plannedQuantity ? parseFloat(formData.plannedQuantity) : undefined,
        varianceReason: formData.varianceReason || undefined,
        jobId: formData.jobId || undefined,
        jobName: formData.jobName || undefined,
        operationPhase: formData.operationPhase || undefined,
        consumptionRate: formData.consumptionRate ? parseFloat(formData.consumptionRate) : undefined,
        rateUnit: formData.rateUnit || undefined,
        baseMetric: formData.baseMetric ? parseFloat(formData.baseMetric) : undefined,
        certificateNumbers: formData.certificateNumbers
          ? formData.certificateNumbers.split(',').map((c) => c.trim()).filter(Boolean)
          : undefined,
      };

      const result = await consumptionApi.record(dto);

      if (result.data.requiresApproval) {
        toast.info('Consumption recorded. Awaiting supervisor approval due to variance.');
      } else {
        toast.success('Consumption recorded successfully');
      }

      setFormOpen(false);
      setFormData(emptyFormData);
      setVariancePercentage(0);
      setRequiresApproval(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<ConsumptionRecord>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (row: ConsumptionRecord) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="font-medium">{row.item.name}</p>
            <p className="text-xs text-muted-foreground">{row.item.sku}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (row: ConsumptionRecord) => (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span>{row.location.name}</span>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (row: ConsumptionRecord) => (
        <span className="font-medium">
          {row.quantity} {row.unit}
        </span>
      ),
    },
    {
      key: 'consumedBy',
      header: 'Consumed By',
      render: (row: ConsumptionRecord) => row.consumedBy.fullName,
    },
    {
      key: 'consumedAt',
      header: 'Date',
      render: (row: ConsumptionRecord) => (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span>{new Date(row.consumedAt).toLocaleString()}</span>
        </div>
      ),
    },
    {
      key: 'workOrder',
      header: 'Work Order',
      render: (row: ConsumptionRecord) =>
        row.workOrder ? (
          <span className="text-sm">{row.workOrder.workOrderNumber}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (row: ConsumptionRecord) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleViewDetail(row)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, record: row })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Consumption Tracking"
        description="Track material consumption across locations"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Consumption' },
          { label: 'Tracking' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/consumption/variance')}>
              <BarChart3 className="mr-2 h-4 w-4" />
              Variance Analysis
            </Button>
            <Button variant="outline" onClick={() => navigate('/consumption/approvals')}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Approvals
              {pendingApprovalsCount > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {pendingApprovalsCount}
                </span>
              )}
            </Button>
            <Button onClick={handleOpenForm}>
              <TrendingDown className="mr-2 h-4 w-4" />
              Record Consumption
            </Button>
          </div>
        }
      />

      {/* Dashboard Metrics */}
      <KPIGrid>
        <MetricCard
          label="Monthly Records"
          value={dashboard?.summary.month.count || 0}
          icon={<TrendingDown className="h-5 w-5" />}
          sentiment="info"
          trend={{ direction: 'neutral', value: `${dashboard?.summary.month.quantity?.toFixed(0) || 0} units consumed` }}
        />
        <MetricCard
          label="Average Variance"
          value={`${averageVariance > 0 ? '+' : ''}${averageVariance.toFixed(1)}%`}
          icon={<BarChart3 className="h-5 w-5" />}
          sentiment={averageVariance > 10 ? 'danger' : averageVariance > 5 ? 'warning' : 'success'}
          trend={{
            direction: averageVariance > 0 ? 'up' : averageVariance < 0 ? 'down' : 'neutral',
            value: averageVariance > 0 ? 'Over planned' : averageVariance < 0 ? 'Under planned' : 'On target'
          }}
        />
        <div className="cursor-pointer" onClick={() => navigate('/consumption/approvals')}>
          <MetricCard
            label="Pending Approvals"
            value={pendingApprovalsCount}
            icon={<AlertTriangle className="h-5 w-5" />}
            sentiment={pendingApprovalsCount > 0 ? 'warning' : 'success'}
            trend={{
              direction: pendingApprovalsCount > 0 ? 'up' : 'neutral',
              value: pendingApprovalsCount > 0 ? 'Requires attention' : 'All approved'
            }}
          />
        </div>
        <MetricCard
          label="Top Consumed Item"
          value={dashboard?.topItems[0]?.name || 'N/A'}
          icon={<Package className="h-5 w-5" />}
          sentiment="neutral"
          trend={dashboard?.topItems[0] ? { direction: 'neutral', value: `${dashboard.topItems[0].quantity} ${dashboard.topItems[0].unit}` } : undefined}
        />
      </KPIGrid>

      {/* OFS Quick Actions Card */}
      <Card>
        <CardHeader>
          <h3 className="text-base font-medium text-foreground">OFS Operations Intelligence</h3>
          <CardDescription>
            Industry-specific consumption tracking with variance analysis, forecasting, and approval workflows
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="justify-start h-auto py-4"
              onClick={() => navigate('/consumption/variance')}
            >
              <div className="flex flex-col items-start gap-1 w-full">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  <span className="font-medium">Variance Analysis</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Track planned vs. actual consumption with variance trending
                </span>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start h-auto py-4"
              onClick={() => navigate('/consumption/approvals')}
            >
              <div className="flex flex-col items-start gap-1 w-full">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  <span className="font-medium">Approval Workflow</span>
                  {pendingApprovalsCount > 0 && (
                    <Badge variant="destructive">{pendingApprovalsCount}</Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  Review and approve consumption records exceeding variance threshold
                </span>
              </div>
            </Button>

            <div className="flex flex-col gap-2 p-4 border rounded-lg">
              <div className="flex items-center gap-2 text-sm font-medium">
                <LinkIcon className="h-4 w-4" />
                <span>OFS Features</span>
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Job/Well context tracking</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Rate-based consumption</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Cost object allocation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Material certification</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search items..."
          className="w-full sm:w-64"
        />
        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((location) => (
              <SelectItem key={location.id} value={location.id}>
                {location.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Time" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">Last 7 Days</SelectItem>
            <SelectItem value="month">Last 30 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <DataTable columns={columns} data={records} loading={loading} />

      {/* Record Consumption Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Consumption</DialogTitle>
            <DialogDescription>
              Record material consumption from inventory
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Core Fields */}
            <div className="space-y-2">
              <Label htmlFor="item">Item *</Label>
              <Select
                value={formData.itemId}
                onValueChange={handleItemChange}
                disabled={loadingRefs}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, locationId: v }))}
                disabled={loadingRefs}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* OFS-Specific: Job/Well Context */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-medium">Job / Well Information (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="jobId">Job/Well ID</Label>
                  <Input
                    id="jobId"
                    value={formData.jobId}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, jobId: e.target.value }))
                    }
                    placeholder="e.g., WELL-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="jobName">Job/Well Name</Label>
                  <Input
                    id="jobName"
                    value={formData.jobName}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, jobName: e.target.value }))
                    }
                    placeholder="e.g., Prospect-1 Well-2"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="operationPhase">Operation Phase</Label>
                <Select
                  value={formData.operationPhase}
                  onValueChange={(v) => setFormData((prev) => ({ ...prev, operationPhase: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select operation phase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRILLING">Drilling</SelectItem>
                    <SelectItem value="COMPLETION">Completion</SelectItem>
                    <SelectItem value="PRODUCTION">Production</SelectItem>
                    <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* OFS-Specific: Variance Tracking */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-medium">Planned vs. Actual Tracking</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="plannedQuantity">Planned Quantity</Label>
                  <Input
                    id="plannedQuantity"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.plannedQuantity}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, plannedQuantity: e.target.value }))
                    }
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantity">Actual Quantity *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, quantity: e.target.value }))
                    }
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Input id="unit" value={formData.unit} disabled />
                </div>
              </div>

              {/* Variance Warning */}
              {formData.plannedQuantity && formData.quantity && (
                <div className="space-y-2">
                  {requiresApproval ? (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Approval Required</AlertTitle>
                      <AlertDescription>
                        Variance of {variancePercentage.toFixed(1)}% exceeds 10% threshold.
                        Supervisor approval will be required.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert>
                      <AlertDescription>
                        Variance: {variancePercentage > 0 ? '+' : ''}{variancePercentage.toFixed(1)}%
                        (within acceptable range)
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {requiresApproval && (
                <div className="space-y-2">
                  <Label htmlFor="varianceReason">Variance Reason *</Label>
                  <Select
                    value={formData.varianceReason}
                    onValueChange={(v) => setFormData((prev) => ({ ...prev, varianceReason: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIELD_CONDITION_CHANGE">Field Condition Change</SelectItem>
                      <SelectItem value="EQUIPMENT_ISSUE">Equipment Issue</SelectItem>
                      <SelectItem value="NORMAL_SHRINKAGE">Normal Shrinkage</SelectItem>
                      <SelectItem value="THEFT">Theft/Loss</SelectItem>
                      <SelectItem value="OTHER">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* OFS-Specific: Rate-Based Consumption */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-medium">Rate-Based Consumption (Optional)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="consumptionRate">Consumption Rate</Label>
                  <Input
                    id="consumptionRate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.consumptionRate}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, consumptionRate: e.target.value }))
                    }
                    placeholder="e.g., 1.2"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rateUnit">Rate Unit</Label>
                  <Select
                    value={formData.rateUnit}
                    onValueChange={(v) => setFormData((prev) => ({ ...prev, rateUnit: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PER_METER">Per Meter</SelectItem>
                      <SelectItem value="PER_FOOT">Per Foot</SelectItem>
                      <SelectItem value="PER_DAY">Per Day</SelectItem>
                      <SelectItem value="PER_CYCLE">Per Cycle</SelectItem>
                      <SelectItem value="PER_HOUR">Per Hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="baseMetric">Base Metric</Label>
                  <Input
                    id="baseMetric"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.baseMetric}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, baseMetric: e.target.value }))
                    }
                    placeholder="e.g., 500 ft"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Example: 0.09 bbl/ft × 500 ft drilled = 45 bbl consumed
              </p>
            </div>

            {/* OFS-Specific: Certification */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-medium">Certification (Optional)</h3>
              <div className="space-y-2">
                <Label htmlFor="certificateNumbers">Certificate Numbers</Label>
                <Input
                  id="certificateNumbers"
                  value={formData.certificateNumbers}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, certificateNumbers: e.target.value }))
                  }
                  placeholder="Comma-separated certificate numbers"
                />
                <p className="text-xs text-muted-foreground">
                  Example: CERT-001, CERT-002, MTR-12345
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Optional notes about this consumption"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Recording...' : 'Record Consumption'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Consumption Details</SheetTitle>
            <SheetDescription>
              View details of this consumption record
            </SheetDescription>
          </SheetHeader>

          {selectedRecord && (
            <div className="mt-6 space-y-6">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Item</p>
                <p className="font-medium">{selectedRecord.item.name}</p>
                <p className="text-sm text-muted-foreground">{selectedRecord.item.sku}</p>
              </div>

              {/* Variance Section */}
              {selectedRecord.plannedQuantity && (
                <div className="border-t pt-4 space-y-4">
                  <h3 className="text-sm font-medium">Variance Tracking</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Planned</p>
                      <p className="font-medium">
                        {selectedRecord.plannedQuantity} {selectedRecord.unit}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Actual</p>
                      <p className="font-medium">
                        {selectedRecord.quantity} {selectedRecord.unit}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Variance</p>
                      <p className={`font-medium ${selectedRecord.variancePercentage && selectedRecord.variancePercentage > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {selectedRecord.variancePercentage && selectedRecord.variancePercentage > 0 ? '+' : ''}{selectedRecord.variancePercentage?.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  {selectedRecord.varianceReason && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Variance Reason</p>
                      <p className="text-sm">{selectedRecord.varianceReason}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Approval Status</p>
                    <StatusBadge
                      status={selectedRecord.approvalStatus === 'APPROVED' || selectedRecord.approvalStatus === 'AUTO_APPROVED'
                        ? 'approved'
                        : selectedRecord.approvalStatus === 'PENDING'
                        ? 'pending'
                        : 'rejected'
                      }
                      label={selectedRecord.approvalStatus}
                    />
                  </div>
                </div>
              )}

              {/* Job/Well Info */}
              {(selectedRecord.jobId || selectedRecord.jobName || selectedRecord.operationPhase) && (
                <div className="border-t pt-4 space-y-4">
                  <h3 className="text-sm font-medium">Job / Well Information</h3>
                  {selectedRecord.jobId && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Job/Well ID</p>
                      <p className="font-medium">{selectedRecord.jobId}</p>
                    </div>
                  )}
                  {selectedRecord.jobName && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Job/Well Name</p>
                      <p className="font-medium">{selectedRecord.jobName}</p>
                    </div>
                  )}
                  {selectedRecord.operationPhase && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Operation Phase</p>
                      <p className="font-medium">{selectedRecord.operationPhase}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {!selectedRecord.plannedQuantity && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Quantity</p>
                    <p className="font-medium">
                      {selectedRecord.quantity} {selectedRecord.unit}
                    </p>
                  </div>
                )}
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Category</p>
                  <p className="font-medium">{selectedRecord.item.category}</p>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium">
                  {selectedRecord.location.name} ({selectedRecord.location.code})
                </p>
              </div>

              {/* Rate-Based Consumption */}
              {selectedRecord.consumptionRate && (
                <div className="border-t pt-4 space-y-4">
                  <h3 className="text-sm font-medium">Rate-Based Consumption</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Rate</p>
                      <p className="font-medium">{selectedRecord.consumptionRate}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Unit</p>
                      <p className="font-medium">{selectedRecord.rateUnit}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Base Metric</p>
                      <p className="font-medium">{selectedRecord.baseMetric}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost Tracking */}
              {selectedRecord.totalCost && (
                <div className="border-t pt-4 space-y-2">
                  <h3 className="text-sm font-medium">Cost Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Unit Cost</p>
                      <p className="font-medium">${selectedRecord.unitCost?.toFixed(2)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Total Cost</p>
                      <p className="font-medium">${selectedRecord.totalCost.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Certification */}
              {selectedRecord.certificateNumbers && selectedRecord.certificateNumbers.length > 0 && (
                <div className="border-t pt-4 space-y-2">
                  <h3 className="text-sm font-medium">Certification</h3>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Certificate Numbers</p>
                    <p className="text-sm">{selectedRecord.certificateNumbers.join(', ')}</p>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Consumed By</p>
                <p className="font-medium">{selectedRecord.consumedBy.fullName}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedRecord.consumedBy.email}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Date & Time</p>
                <p className="font-medium">
                  {new Date(selectedRecord.consumedAt).toLocaleString()}
                </p>
              </div>

              {selectedRecord.workOrder && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Work Order</p>
                  <p className="font-medium">
                    {selectedRecord.workOrder.workOrderNumber}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedRecord.workOrder.title}
                  </p>
                </div>
              )}

              {selectedRecord.notes && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        title="Delete Consumption Record"
        description="Are you sure you want to delete this consumption record? The inventory will be restored."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
